/**
 * IndexedDB Implementation
 * Provides a wrapper around IndexedDB for local data storage with TypeScript-like JSDoc definitions
 * @module indexedDB
 */

/**
 * @typedef {Object} StoreConfig
 * @property {string} keyPath - The key path for the store
 * @property {boolean} [autoIncrement] - Whether the store uses auto-incrementing keys
 * @property {Array<IndexConfig>} indexes - Array of index configurations
 */

/**
 * @typedef {Object} IndexConfig
 * @property {string} name - The name of the index
 * @property {string} keyPath - The key path for the index
 * @property {boolean} unique - Whether the index should enforce unique values
 */

// Database name and version from centralized config
const DB_NAME = 'metahealth'
const DB_VERSION = 2

// Store names
export const STORES = {
  SETTINGS: 'settings',
  SYNC_QUEUE: 'syncQueue',
  // Custom Stores
  ENTITIES: 'entities',
  MEDICAL_HISTORY: 'medicalHistory',
  AUTH: 'auth',
  KEYWORDS: 'keywords',
  SERVICES: 'services',
  VIRTUAL_SERVICES: 'virtualServices',
  CLINICS: 'clinics',
  TERMS: 'terms',
  CONFIG: 'config',
  PATIENTS: 'patients',
  SYSTEM_USERS: 'systemUsers',
  AUTH_USERS: 'authUsers',
  COUNTRIES: 'countries'
}

// Store configurations
const STORE_CONFIGS = {
  [STORES.SETTINGS]: {
    keyPath: 'key',
    indexes: []
  },
  [STORES.SYNC_QUEUE]: {
    keyPath: 'id',
    autoIncrement: true,
    indexes: [
      { name: 'createdAt', keyPath: 'createdAt', unique: false },
      { name: 'retryCount', keyPath: 'retryCount', unique: false },
      { name: 'status', keyPath: 'status', unique: false }
    ]
  },
  [STORES.AUTH]: {
    keyPath: 'id',
    indexes: [{ name: 'lastUpdated', keyPath: 'lastUpdated', unique: false }]
  },
  [STORES.KEYWORDS]: {
    keyPath: 'key',
    indexes: [
      { name: 'syncStatus', keyPath: 'syncStatus', unique: false },
      { name: 'createdAt', keyPath: 'createdAt', unique: false }
    ]
  },
  // MongoDB-style stores (using _id)
  [STORES.ENTITIES]: {
    keyPath: '_id',
    indexes: [{ name: 'syncStatus', keyPath: 'syncStatus', unique: false }]
  },
  [STORES.MEDICAL_HISTORY]: {
    keyPath: '_id',
    indexes: [
      { name: 'syncStatus', keyPath: 'syncStatus', unique: false },
      { name: 'createdAt', keyPath: 'createdAt', unique: false }
    ]
  },
  [STORES.SERVICES]: {
    keyPath: '_id',
    indexes: [
      { name: 'syncStatus', keyPath: 'syncStatus', unique: false },
      { name: 'createdAt', keyPath: 'createdAt', unique: false }
    ]
  },
  [STORES.VIRTUAL_SERVICES]: {
    keyPath: '_id',
    indexes: [
      { name: 'syncStatus', keyPath: 'syncStatus', unique: false },
      { name: 'createdAt', keyPath: 'createdAt', unique: false }
    ]
  },
  [STORES.CLINICS]: {
    keyPath: '_id',
    indexes: [{ name: 'syncStatus', keyPath: 'syncStatus', unique: false }]
  },
  [STORES.TERMS]: {
    keyPath: '_id',
    indexes: [
      { name: 'syncStatus', keyPath: 'syncStatus', unique: false },
      { name: 'createdAt', keyPath: 'createdAt', unique: false }
    ]
  },
  [STORES.CONFIG]: {
    keyPath: '_id',
    indexes: [
      { name: 'syncStatus', keyPath: 'syncStatus', unique: false },
      { name: 'createdAt', keyPath: 'createdAt', unique: false }
    ]
  },
  [STORES.PATIENTS]: {
    keyPath: 'userRef',
    indexes: [
      { name: 'syncStatus', keyPath: 'syncStatus', unique: false },
      { name: 'createdAt', keyPath: 'createdAt', unique: false }
    ]
  },
  [STORES.SYSTEM_USERS]: {
    keyPath: '_id',
    indexes: [
      { name: 'syncStatus', keyPath: 'syncStatus', unique: false },
      { name: 'createdAt', keyPath: 'createdAt', unique: false }
    ]
  },
  [STORES.AUTH_USERS]: {
    keyPath: '_id',
    indexes: [
      { name: 'syncStatus', keyPath: 'syncStatus', unique: false },
      { name: 'createdAt', keyPath: 'createdAt', unique: false }
    ]
  },
  [STORES.COUNTRIES]: {
    keyPath: '_id',
    indexes: [{ name: 'syncStatus', keyPath: 'syncStatus', unique: false }]
  }
}

/**
 * Opens the IndexedDB database
 * @returns {Promise<IDBDatabase>} The database instance
 * @throws {Error} If the database cannot be opened
 */
export const openDatabase = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = (event) => {
      reject(new Error(`Failed to open IndexedDB: ${event.target.error}`))
    }

    request.onsuccess = (event) => {
      resolve(event.target.result)
    }

    request.onupgradeneeded = (event) => {
      const db = event.target.result

      // Create or update all stores based on configuration
      Object.entries(STORE_CONFIGS).forEach(([storeName, config]) => {
        if (!db.objectStoreNames.contains(storeName)) {
          const store = db.createObjectStore(storeName, {
            keyPath: config.keyPath,
            autoIncrement: config.autoIncrement
          })

          // Create indexes
          config.indexes.forEach((index) => {
            if (!store.indexNames.contains(index.name)) {
              store.createIndex(index.name, index.keyPath, { unique: index.unique })
            }
          })
        }
      })
    }
  })
}

/**
 * Gets the configuration for a store
 * @param {string} storeName - The name of the store
 * @returns {StoreConfig} The store configuration
 * @throws {Error} If no configuration is found for the store
 */
export const getStoreConfig = (storeName) => {
  const config = STORE_CONFIGS[storeName]
  if (!config) {
    throw new Error(`No configuration found for store: ${storeName}`)
  }
  return config
}

/**
 * Gets the key path for a store
 * @param {string} storeName - The name of the store
 * @returns {string} The key path
 */
export const getStoreKeyPath = (storeName) => {
  return getStoreConfig(storeName).keyPath
}

/**
 * Adds an item to a store
 * @param {string} storeName - The name of the store
 * @param {Object} item - The item to add
 * @returns {Promise<string|number>} The ID of the added item
 */
export const addItem = async (storeName, item) => {
  const db = await openDatabase()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite')
    const store = transaction.objectStore(storeName)

    // Add timestamp for new items
    const itemWithTimestamp = {
      ...item,
      createdAt: item.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    const request = store.add(itemWithTimestamp)

    request.onsuccess = (event) => {
      resolve(event.target.result)
    }

    request.onerror = (event) => {
      reject(new Error(`Failed to add item to ${storeName}: ${event.target.error}`))
    }

    transaction.oncomplete = () => {
      db.close()
    }
  })
}

/**
 * Gets all items from a store
 * @param {string} storeName - The name of the store
 * @returns {Promise<Array>} Array of items
 */
export const getAllItems = async (storeName) => {
  const db = await openDatabase()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly')
    const store = transaction.objectStore(storeName)
    const request = store.getAll()

    request.onsuccess = (event) => {
      resolve(event.target.result)
    }

    request.onerror = (event) => {
      reject(new Error(`Failed to get items from ${storeName}: ${event.target.error}`))
    }

    transaction.oncomplete = () => {
      db.close()
    }
  })
}

/**
 * Gets an item by ID
 * @param {string} storeName - The name of the store
 * @param {string|number} id - The ID of the item
 * @returns {Promise<Object|undefined>} The item or undefined if not found
 */
export const getItemById = async (storeName, id) => {
  const db = await openDatabase()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly')
    const store = transaction.objectStore(storeName)
    const request = store.get(id)

    request.onsuccess = (event) => {
      resolve(event.target.result)
    }

    request.onerror = (event) => {
      reject(new Error(`Failed to get item from ${storeName}: ${event.target.error}`))
    }

    transaction.oncomplete = () => {
      db.close()
    }
  })
}

/**
 * Updates an item
 * @param {string} storeName - The name of the store
 * @param {Object} item - The item to update
 * @returns {Promise<Object>} The updated item
 */
export const updateItem = async (storeName, item) => {
  const db = await openDatabase()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite')
    const store = transaction.objectStore(storeName)

    // Add updated timestamp
    const updatedItem = {
      ...item,
      updatedAt: new Date().toISOString()
    }

    const request = store.put(updatedItem)

    request.onsuccess = () => {
      resolve(updatedItem)
    }

    request.onerror = (event) => {
      reject(new Error(`Failed to update item in ${storeName}: ${event.target.error}`))
    }

    transaction.oncomplete = () => {
      db.close()
    }
  })
}

/**
 * Deletes an item
 * @param {string} storeName - The name of the store
 * @param {string|number} id - The ID of the item
 * @returns {Promise<void>}
 */
export const deleteItem = async (storeName, id) => {
  const db = await openDatabase()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite')
    const store = transaction.objectStore(storeName)
    const request = store.delete(id)

    request.onsuccess = () => {
      resolve()
    }

    request.onerror = (event) => {
      reject(new Error(`Failed to delete item from ${storeName}: ${event.target.error}`))
    }

    transaction.oncomplete = () => {
      db.close()
    }
  })
}

/**
 * Gets items by index
 * @param {string} storeName - The name of the store
 * @param {string} indexName - The name of the index
 * @param {*} value - The value to search for
 * @returns {Promise<Array>} Array of matching items
 */
export const getItemsByIndex = async (storeName, indexName, value) => {
  const db = await openDatabase()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly')
    const store = transaction.objectStore(storeName)
    const index = store.index(indexName)
    const request = index.getAll(value)

    request.onsuccess = (event) => {
      resolve(event.target.result)
    }

    request.onerror = (event) => {
      reject(new Error(`Failed to get items by index from ${storeName}: ${event.target.error}`))
    }

    transaction.oncomplete = () => {
      db.close()
    }
  })
}

/**
 * Checks and fixes database structure
 * @returns {Promise<boolean>} Success status
 */
export const checkAndFixDatabase = async () => {
  try {
    const databases = await window.indexedDB.databases()
    const ourDb = databases.find((db) => db.name === DB_NAME)

    if (ourDb && ourDb.version !== DB_VERSION) {
      // Close any open connections before deletion
      const closeRequest = indexedDB.open(DB_NAME)
      await new Promise((resolve) => {
        closeRequest.onsuccess = (event) => {
          const db = event.target.result
          db.close()
          resolve()
        }
        closeRequest.onerror = resolve // Continue even if there's an error
      })

      // Delete the database
      await new Promise((resolve, reject) => {
        const deleteRequest = window.indexedDB.deleteDatabase(DB_NAME)
        deleteRequest.onsuccess = () => {
          resolve()
        }
        deleteRequest.onerror = (event) => {
          console.error('Error deleting database:', event.target.error)
          reject(new Error(`Failed to delete database: ${event.target.error}`))
        }
      })

      // Create a new database with the correct version
      await new Promise((resolve, reject) => {
        const openRequest = indexedDB.open(DB_NAME, DB_VERSION)

        openRequest.onupgradeneeded = (event) => {
          const db = event.target.result

          // Create all stores based on configuration
          Object.entries(STORE_CONFIGS).forEach(([storeName, config]) => {
            if (!db.objectStoreNames.contains(storeName)) {
              const store = db.createObjectStore(storeName, {
                keyPath: config.keyPath,
                autoIncrement: config.autoIncrement
              })

              // Create indexes
              config.indexes.forEach((index) => {
                if (!store.indexNames.contains(index.name)) {
                  store.createIndex(index.name, index.keyPath, { unique: index.unique })
                }
              })
            }
          })
        }

        openRequest.onsuccess = (event) => {
          event.target.result.close()
          resolve()
        }

        openRequest.onerror = (event) => {
          console.error('Error creating database:', event.target.error)
          reject(new Error(`Failed to create database: ${event.target.error}`))
        }
      })
    } else {
    }

    return true
  } catch (error) {
    console.error('Error in checkAndFixDatabase:', error)
    return false
  }
}

/**
 * Clears all data from all stores
 * @returns {Promise<boolean>} Success status
 */
export const clearAllData = async () => {
  try {
    await Promise.all([clearStore(STORES.ENTITIES), clearStore(STORES.SYNC_QUEUE), clearStore(STORES.SETTINGS)])
    return true
  } catch {
    return false
  }
}

/**
 * Clears all items from a store
 * @param {string} storeName - The name of the store
 * @returns {Promise<void>}
 */
export const clearStore = async (storeName) => {
  const db = await openDatabase()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite')
    const store = transaction.objectStore(storeName)
    const request = store.clear()

    request.onsuccess = resolve
    request.onerror = reject

    transaction.oncomplete = () => {
      db.close()
    }
  })
}
